/**
 * QuoteRefuseDialog — Phase 2B Item H.
 *
 * Refuse un devis (statut envoyé) avec les mêmes LOSS_REASONS que
 * `mark-lost` sur lead (cohérence Prod + widget dashboard "Pourquoi on perd").
 *   - 6 radios enum
 *   - Textarea `detail` obligatoire uniquement si reason == "other"
 *   - Backend cascade : status → refuse, interaction timeline devis_refuse,
 *     annule les reminders devis, cascade lead → perdu si dernier devis actif.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const REASONS = [
  { value: "price_too_high", label: "Prix trop élevé" },
  { value: "went_competitor", label: "Parti chez un concurrent" },
  { value: "abandoned", label: "Projet abandonné" },
  { value: "delay_too_long", label: "Délai trop long" },
  { value: "no_response", label: "Sans réponse" },
  { value: "other", label: "Autre (précisez)" },
];

export default function QuoteRefuseDialog({ open, onOpenChange, quote, onSuccess }) {
  const [reason, setReason] = useState("price_too_high");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("price_too_high");
      setDetail("");
    }
  }, [open]);

  const detailRequired = reason === "other";
  const detailTooShort = detailRequired && detail.trim().length < 5;
  const submitDisabled = submitting || detailTooShort;

  const handleSubmit = async () => {
    if (detailTooShort) {
      toast.error("Détail requis (5 caractères minimum) pour la raison Autre");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/quotes/${quote.id}/refuse`, {
        reason,
        detail: detail.trim() || null,
      });
      toast.success("Devis refusé — cascade lead effectuée si dernier devis actif");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="quote-refuse-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Refuser le devis</DialogTitle>
          <DialogDescription>
            Le motif nourrit le widget « Pourquoi on perd » du dashboard et
            propage la perte au lead s&apos;il n&apos;a plus de devis actif.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={reason}
          onValueChange={setReason}
          className="space-y-2"
          data-testid="quote-refuse-reasons"
        >
          {REASONS.map((r) => (
            <div key={r.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={r.value}
                id={`refuse-reason-${r.value}`}
                data-testid={`quote-refuse-radio-${r.value}`}
              />
              <Label
                htmlFor={`refuse-reason-${r.value}`}
                className="cursor-pointer text-sm text-slate-200"
              >
                {r.label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <div className="space-y-1">
          {reason === "other" && (
            <>
              <Label
                htmlFor="refuse-detail"
                className="text-xs text-slate-200"
              >
                Précisions (obligatoire, min 5 caractères)
              </Label>
              <Textarea
                id="refuse-detail"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Précisez la raison du refus…"
                rows={3}
                maxLength={500}
                className={detailTooShort ? "border-red-500/50" : ""}
                data-testid="quote-refuse-detail"
              />
              {detailTooShort && (
                <p className="text-xs text-red-400">
                  Ajoutez au moins 5 caractères de contexte.
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="quote-refuse-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="min-w-[140px] bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
            data-testid="quote-refuse-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Refus en cours…
              </>
            ) : (
              "Confirmer le refus"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
